export type RuntimeEnv = Record<string, string | undefined>;

declare global {
  var __NEOGENIUX_RUNTIME_ENV__: RuntimeEnv | undefined;
}

const readRuntimeEnv = (): RuntimeEnv => {
  // Build-time defaults baked in by Vite (via main.tsx merging
  // `import.meta.env` into `globalThis.__NEOGENIUX_RUNTIME_ENV__`).
  // This carries every `VITE_PUBLIC_*` from `apps/neocrm/.env` —
  // S3 credentials, AWS region, landing URL, etc.
  const buildTimeEnv =
    typeof globalThis.__NEOGENIUX_RUNTIME_ENV__ === 'object' &&
    globalThis.__NEOGENIUX_RUNTIME_ENV__ !== null
      ? globalThis.__NEOGENIUX_RUNTIME_ENV__
      : {};

  if (Object.keys(buildTimeEnv).length > 0) {
    return buildTimeEnv;
  }

  // Jest/node environment fallback
  if (typeof process !== 'undefined' && process.env) {
    return process.env as unknown as RuntimeEnv;
  }

  return {};
};

export const getEnv = (key: string): string | undefined => {
  return readRuntimeEnv()[key];
};

export const getEnvOrDefault = (key: string, fallback: string): string => {
  return getEnv(key) || fallback;
};

export const getApiBaseUrl = (): string => {
  const configured = getEnv('VITE_API_BASE_URL');
  const configuredTrimmed = configured?.trim();

  const isBrowser = typeof window !== 'undefined';
  const currentHostname = isBrowser ? window.location.hostname : '';
  const isRunningLocally =
    currentHostname === 'localhost' || currentHostname === '127.0.0.1';

  const isLocalhostTarget = Boolean(
    configuredTrimmed &&
      /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(
        configuredTrimmed
      )
  );

  // Prefer explicit configuration. If build-time env was mistakenly left as
  // localhost for a real deployment, do NOT call the user's machine.
  if (configuredTrimmed && (!isLocalhostTarget || isRunningLocally)) {
    return configuredTrimmed;
  }

  // Local dev default
  if (isRunningLocally) {
    return 'http://localhost:3001/api';
  }

  // Production fallback: infer api.<baseDomain>/api
  // Example: crm.neogeniux.com -> api.neogeniux.com/api
  if (isBrowser) {
    const hostname = window.location.hostname;
    const parts = hostname.split('.').filter(Boolean);
    const baseDomain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
    return `${window.location.protocol}//api.${baseDomain}/api`;
  }

  return 'http://localhost:3001/api';
};

export const getAppUrl = (): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return getEnvOrDefault('VITE_APP_URL', origin);
};

/**
 * Public marketing site (neolanding).
 * - Dev: http://localhost:3000
 * - Prod: https://app.neogeniux.com (configurable via VITE_LANDING_URL)
 */
export const getLandingUrl = (): string => {
  const configured = getEnvOrDefault('VITE_LANDING_URL', '');
  if (configured) return configured;
  const isBrowser = typeof window !== 'undefined';
  if (!isBrowser) return 'https://app.neogeniux.com';
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://app.neogeniux.com';
};
